import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import Dropdown from '../../common/Dropdown'
import { toast } from '../../common/Toast'
import { productModelsWritePermissions } from '../../../utils/permissionValidation'
import { deleteImagesSignedUrl, getImagesSignedUrl, putAdminModelImage } from '../../../utils/requests/productModelsAPI'
import { clearProductModelsMessages, createProductModelsImages } from '../../../actions/productModels'
import { loadModels } from '../../../actions/shared'
import ImageContainer from './ImageContainer'
import '../uploads.scss'

const toSlug = (v) =>
  String(v || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9-_]/g, '')

const readImageDims = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.naturalWidth || 0, height: img.naturalHeight || 0 })
      URL.revokeObjectURL(url)
    }
    img.onerror = (e) => {
      URL.revokeObjectURL(url)
      reject(e)
    }
    img.src = url
  })

const EMPTY_ENTRY = () => ({
  id: Date.now() + Math.random(),
  image: null,
})

const AddModelImageModal = (props) => {
  const { t } = useTranslation()
  const { closeModal, showSpinner, setShowSpinner } = props
  const [deviceModelId, setDeviceModelId] = useState('')
  const [categoryName, setCategoryName] = useState('')
  const [productType, setProductType] = useState('')
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})
  const [entries, setEntries] = useState(Array.from({ length: 8 }, () => EMPTY_ENTRY()))

  const modelOptions = useMemo(() => {
    return (props.models || [])
      .map((m) => ({
        key: m?.id ?? '',
        value: (m?.model ?? m?.name ?? '').toString(),
        raw: m,
      }))
      .filter((o) => o.key && o.value)
  }, [props.models])

  const selectedModel = useMemo(() => {
    return modelOptions.find((o) => String(o.key) === String(deviceModelId))?.raw || null
  }, [modelOptions, deviceModelId])

  useEffect(() => {
    if (!props.sharedModels || props.sharedModels.length === 0) {
      props.actions.loadModels()
    }
  }, [])

  useEffect(() => {
    if (!deviceModelId) {
      setCategoryName('')
      setProductType('')
      return
    }

    const pm = selectedModel
    const pmIdStr = pm?.id != null ? String(pm.id) : ''
    const pmModelIdStr =
      pm?.model_id != null ? String(pm.model_id) : pm?.modelId != null ? String(pm.modelId) : pm?.deviceModelId != null ? String(pm.deviceModelId) : ''
    const pmModelName = String(pm?.model ?? pm?.name ?? '')
      .trim()
      .toLowerCase()
    const match =
      (props.sharedModels || []).find((s) => String(s?.model_id || '') === pmIdStr) ||
      (props.sharedModels || []).find((s) => String(s?.model_id || '') === pmModelIdStr) ||
      (props.sharedModels || []).find(
        (s) =>
          String(s?.model || '')
            .trim()
            .toLowerCase() === pmModelName,
      )
    const derivedCategory = match?.product_category || match?.category || ''
    const derivedType = match?.product_type || match?.type || ''
    setCategoryName(String(derivedCategory || ''))
    setProductType(String(derivedType || ''))
  }, [deviceModelId, selectedModel, props.sharedModels])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)
      props.actions.clearProductModelsMessages()
    }
  }, [props.errorMessage])

  const updateEntry = (idx, patch) => {
    setEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)))
  }

  const handleUploadImage = async (idx, newImage) => {
    const file = newImage?.blob
    if (!file) return
    const fileName = file.name || ''
    let dims = { width: 0, height: 0 }
    try {
      dims = await readImageDims(file)
    } catch {}
    updateEntry(idx, {
      image: {
        ...newImage,
        fileName,
        fileSize: file.size || 0,
        width: dims.width || 0,
        height: dims.height || 0,
        extension: (fileName.split('.').pop() || newImage?.extension || '').toLowerCase(),
      },
    })
  }

  const handleRemoveImage = (idx) => {
    updateEntry(idx, { image: null })
  }

  const buildFolderPath = () => {
    const categorySlug = toSlug(categoryName)
    const productTypeSlug = toSlug(productType)
    return `images/${categorySlug}/${productTypeSlug}`
  }

  const buildImageTitle = (entry) => {
    const modelSlug = toSlug(selectedModel?.model || selectedModel?.name || '')
    const w = Number(entry?.image?.width || 0)
    const h = Number(entry?.image?.height || 0)
    return `${modelSlug}_${w}x${h}`
  }

  const validate = () => {
    const next = {}
    if (!deviceModelId) next.deviceModelId = true
    if (!categoryName?.trim()) next.categoryName = true
    if (!productType?.trim()) next.productType = true
    const hasAnyImage = entries.some((e) => !!e?.image?.blob)
    if (!hasAnyImage) next.images = true
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSave = async () => {
    if (!validate()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    setSaving(true)
    setShowSpinner?.(true)

    try {
      const folderPath = buildFolderPath()
      const toUpload = entries.filter((e) => e?.image?.blob)
      let uploadedCount = 0

      for (const entry of toUpload) {
        const file = entry.image.blob
        const finalImageTitle = buildImageTitle(entry)
        const finalFileName = entry?.image?.fileName
        const ext = entry?.image?.extension || (finalFileName.split('.').pop() || '').toLowerCase()
        const queryParams = {
          filename: finalFileName,
          filepath: folderPath,
        }
        const signed = await getImagesSignedUrl(queryParams)

        await putAdminModelImage(signed.url, file, ext)
        const storedPath = signed.path || `${folderPath}/${finalFileName}`

        try {
          await props.actions.createProductModelsImages(deviceModelId, {
            fileName: finalFileName,
            filePath: folderPath,
            url: storedPath,
            fileSize: Number(entry?.image?.fileSize || file.size || 0),
            height: Number(entry?.image?.height || 0),
            width: Number(entry?.image?.width || 0),
            imageTitle: finalImageTitle,
            fileType: ext,
          })
          uploadedCount += 1
        } catch (dbError) {
          await deleteImagesSignedUrl({
            filepath: folderPath,
            filename: signed.fileName,
          })
        }
      }

      if (uploadedCount > 0) {
        toast.success(t('product_models_context.image_added_successfully'))
      }
      props.onSuccess?.()
      closeModal()
    } catch (e) {
      console.error(t('error.something_wrong'))
    } finally {
      setSaving(false)
      setShowSpinner?.(false)
      props.actions.clearProductModelsMessages?.()
    }
  }

  return (
    <PageModal
      toggle
      onToggle={closeModal}
      className={`uploads-modal add-model-image-modal ${deviceModelId ? '' : 'half-modal'}`}
      title={t('product_models_context.add_new_image')}
      footerContent={
        <>
          <SimpleButton className="cancel-button" onClick={closeModal}>
            {t('cancel')}
          </SimpleButton>
          <SimpleButton className="submit-button" onClick={handleSave} disabled={saving || !!showSpinner} requiredPermission={productModelsWritePermissions}>
            {saving ? t('saving') : t('save')}
          </SimpleButton>
        </>
      }
    >
      <div className="uploads-modal-content">
        <div className="section-title">{t('product_models_context.general_model_details').toUpperCase()}</div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option flex-justify-start">{`${t('model')}*`}</p>
            <Dropdown
              className="dropdown-input"
              placeholder={t('please_select')}
              options={modelOptions.map((o) => ({ key: o.key, value: o.value }))}
              initialSelectedKey={deviceModelId || undefined}
              onChange={(opt) => setDeviceModelId(opt.key)}
              hasError={errors.deviceModelId}
            />
          </div>
          <div className="input-wrapper" />
        </div>

        {deviceModelId && (
          <>
            <div className="separator" />
            <div className="section-title">{t('product_models_context.image_details')?.toUpperCase?.() || t('product_models_context.image_details')}</div>
            <div className="images-instruction-box-text">{t('product_models_context.images_instructions')}</div>
            {errors.images && <div style={{ marginTop: 8 }}></div>}

            <div className="product-images-wrapper">
              <div className="images-wrapper">
                {Array.from({ length: 8 }).map((_, idx) => {
                  const entry = entries[idx]
                  return (
                    <div key={entry.id || idx}>
                      <ImageContainer
                        index={idx}
                        fileSrc={entry?.image?.imageSrc}
                        canUpload={idx === 0 ? !entries[0]?.image?.imageSrc : !!entries[idx - 1]?.image?.imageSrc && !entries[idx]?.image?.imageSrc}
                        hasError={!!errors.images}
                        handleUploadImage={(img) => handleUploadImage(idx, img)}
                        handleRemoveImage={() => handleRemoveImage(idx)}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </PageModal>
  )
}

function stateToProps({ productModels, shared }) {
  return {
    models: productModels?.productModelsFilterOptions?.models || [],
    sharedModels: shared?.models || [],
    isLoading: productModels?.isLoading,
    errorMessage: productModels?.errorMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        createProductModelsImages,
        clearProductModelsMessages,
        loadModels,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(AddModelImageModal)
